<!-- src/components/PoiDetailDialog.vue ------------------------------------ -->
<template>
  <div
    class="modal fade"
    id="poi-detail-dialog"
    tabindex="-1"
    role="dialog"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <!-- ─────────────────────────────────────────────────────────── HEADER -->
        <div
          class="modal-header border-0 pt-3 pe-3"
          style="position: relative;"
        >
          <button
            type="button"
            class="btn p-0"
            data-bs-dismiss="modal"
            aria-label="Close"
            style="position: absolute; right: 1rem; top: 1rem; z-index: 1;"
          >
            <span class="material-icons">close</span>
          </button>
        </div>

        <!-- ─────────────────────────────────────────────────────────── BODY -->
        <div class="px-3 pb-3 modal-body">
          <!-- 0️⃣  Fallback (no SerpApi hit) -->
          <template v-if="!serpResult">
            <p v-if="name" class="display-5"><strong>{{ name }}</strong></p>
            <p v-if="!name" class="display-5"><strong>{{ type }}</strong></p>
            <p v-if="name" class="display-5">{{ type }}</p>

            <p><a :href="coordsLink"><small class="text-muted">{{ coords }}</small></a></p>

            <p v-if="phoneNumber" class="d-flex align-items-center mb-2">
              <span class="material-icons mr-2">call</span> {{ phoneNumber }}
            </p>

            <p v-if="email" class="d-flex align-items-center mb-2">
              <span class="material-icons mr-2">mail</span> {{ email }}
            </p>

            <hr v-if="description" class="my-2" />
            <p v-if="description">{{ description }}</p>

            <div v-if="poi" class="mt-3 d-flex gap-2 flex-wrap">
              <button
                class="btn btn-sm btn-primary rounded-pill p-1 px-2 d-flex align-items-center"
                @click="addToWaypoints"
              >
                ADD TO WAYPOINTS <span class="material-icons ml-1">add_circle</span>
              </button>
              <button
                v-if="showAddControlPoint"
                class="btn btn-sm btn-secondary rounded-pill p-1 px-2 d-flex align-items-center"
                @click="addControlPoint"
              >
                ADD CONTROL POINT <span class="material-icons ml-1">place</span>
              </button>
              <button
                v-if="showMakeHighlight"
                class="btn btn-sm btn-warning rounded-pill p-1 px-2 d-flex align-items-center"
                @click="makeHikeHighlight"
              >
                MAKE HIKE HIGHLIGHT <span class="material-icons ml-1">star</span>
              </button>
            </div>
          </template>

          <!-- 1️⃣  SerpApi-enriched body -->
          <template v-else>
            <!-- Name (visible to everyone) -->
            <p class="display-5"><strong>{{ serpName }}</strong></p>

            <!-- Rating (visible to everyone) -->
            <div class="small text-muted mb-3" v-if="serpRating > 0">
              <span
                v-for="n in Math.round(serpRating)"
                :key="'filled-' + n"
                class="text-warning"
              >
                &#9733;
              </span>
              <span
                v-for="n in 5 - Math.round(serpRating)"
                :key="'empty-' + n"
                class="text-secondary"
              >
                &#9733;
              </span>
              <span class="ms-2">
                {{ serpRating.toFixed(1) }} ({{ serpTotalRatings }} reviews)
              </span>
            </div>

            <div class="serp-wrapper">
              <!-- Blur overlay for non-Pro+ users (outside blurred container) -->
              <div v-if="shouldBlurSerpData" class="serp-overlay">
                <button class="btn btn-yellow" @click="goToPurchasePage">
                  {{ viewLocationButtonText }}
                </button>
              </div>

              <div class="serp-data-container" :class="{ 'serp-blurred': shouldBlurSerpData }">
                <!-- Phone / Website / Address -->
            <p class="d-flex align-items-center mb-2" v-if="serpPhone">
              <span class="material-icons mr-3">call</span> {{ serpPhone }}
            </p>

            <p class="d-flex align-items-center mb-2" v-if="email">
              <span class="material-icons mr-3">mail</span> {{ email }}
            </p>

            <p class="d-flex align-items-center mb-2" v-if="serpWebsite">
              <span class="material-icons mr-3">public</span>
              <a :href="serpWebsite" target="_blank" rel="noopener noreferrer">
                <u class="text-yellow">Website</u>
              </a>
            </p>

            <p class="d-flex align-items-center mb-2" v-if="serpAddress">
              <span class="material-icons mr-3">location_on</span> {{ serpAddress }}
            </p>

            <!-- Waypoint button -->
            <div v-if="poi" class="mt-3 d-flex gap-2 flex-wrap">
              <button
                class="btn btn-sm btn-primary rounded-pill p-1 px-2 d-flex align-items-center"
                @click="addToWaypoints"
              >
                ADD TO WAYPOINTS <span class="material-icons ml-1">add_circle</span>
              </button>
              <button
                v-if="showAddControlPoint"
                class="btn btn-sm btn-secondary rounded-pill p-1 px-2 d-flex align-items-center"
                @click="addControlPoint"
              >
                ADD CONTROL POINT <span class="material-icons ml-1">place</span>
              </button>
              <button
                v-if="showMakeHighlight"
                class="btn btn-sm btn-warning rounded-pill p-1 px-2 d-flex align-items-center"
                @click="makeHikeHighlight"
              >
                MAKE HIKE HIGHLIGHT <span class="material-icons ml-1">star</span>
              </button>
            </div>

            <!-- POI description (your own DB) -->
            <hr v-if="description" class="my-2" />
            <p v-if="description">{{ description }}</p>
            <hr class="my-2" />

            <!-- 1.a  Photos carousel -->
            <template v-if="serpImages.length">
              <div
                id="serpImagesCarousel"
                class="carousel slide mb-3"
                data-bs-ride="carousel"
              >
                <div class="carousel-inner">
                  <div
                    v-for="(img, idx) in serpImages"
                    :key="idx"
                    :class="['carousel-item', { active: idx === 0 }]"
                  >
                    <img
                      :src="photoSrc(img)"
                      class="d-block w-100 rounded-lg"
                      style="object-fit: cover; max-height: 300px;"
                      :alt="serpName"
                    />
                  </div>
                </div>
                <button
                  class="carousel-control-prev"
                  type="button"
                  data-bs-target="#serpImagesCarousel"
                  data-bs-slide="prev"
                >
                  <span class="carousel-control-prev-icon"></span>
                  <span class="visually-hidden">Prev</span>
                </button>
                <button
                  class="carousel-control-next"
                  type="button"
                  data-bs-target="#serpImagesCarousel"
                  data-bs-slide="next"
                >
                  <span class="carousel-control-next-icon"></span>
                  <span class="visually-hidden">Next</span>
                </button>
              </div>
            </template>

            <!-- 1.b  Reviews -->
            <div v-if="serpReviews.length">
              <h6 class="mt-3">Reviews</h6>
              <div
                v-for="(rev, idx) in serpReviews.slice(0, 5)"
                :key="idx"
                class="card mb-2"
              >
                <div class="card-body p-2">
                  <div class="d-flex align-items-center">
                    <strong>{{ rev.author }}</strong>
                    <div class="ms-2">
                      <span
                        v-for="n in rev.rating"
                        :key="`star-${idx}-${n}`"
                        class="text-warning"
                      >
                        &#9733;
                      </span>
                    </div>
                  </div>
                  <div class="small text-muted">{{ rev.date }}</div>
                  <p class="mb-1 mt-1">{{ rev.text }}</p>
                </div>
              </div>
            </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <Loader v-if="showLoader" />

    <!-- Toast for free views notifications -->
    <div class="position-fixed top-0 end-0 p-3" style="z-index: 9999">
      <div
        id="serpFreeViewToast"
        class="toast"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      >
        <div class="toast-header bg-yellow">
          <strong class="me-auto">Free Location View</strong>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        </div>
        <div class="toast-body">
          {{ toastMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { Modal, Toast } from 'bootstrap';
import graph_client from '@/utils/graphql/graph_client';
import { GetPOIByID } from '@/utils/graphql/poi-graph';
import { serpClient } from '@/utils/services/SerpApiClient';
import Loader from '@/utils/shared_components/Loader.vue';
import { OnBoardingGoal } from '@/utils/models/OnBoardingGoal';
import { POIUtility } from '@/utils/models/POI';
import { poiDetailsToWaypointDescriptionHelper } from '@/utils/helpers/PoiDetailsToWaypointDescriptionHelper';
import { poiToHikeHighlight } from '@/utils/helpers/PoiToHikeHighlightHelper';
import {UPDATE_ACCOMMODATION_MUTATION, UPDATE_AMENITY_MUTATION} from "../../utils/graphql/poi-graph";
import { RevenueCatWrapper } from '@/utils/RevenueCatWrapper';

export default defineComponent({
  components: { Loader },

  data() {
    return {
      poi: null,
      poiId: null,
      poiType: null,
      serpData: null,
      showLoader: false,
      isProPlus: false,
      viewedPoiIds: [],
      toastMessage: '',
      freeViewsRemaining: 2,
    };
  },

  async mounted() {
    this.isProPlus = await RevenueCatWrapper.isProOrProPlus();
    this.loadViewedPoiIds();
    this.freeViewsRemaining = this.getFreeViewsRemaining();

    const element = document.getElementById('poi-detail-dialog');
    const modal = new Modal(element);

    element.addEventListener('hidden.bs.modal', this.onModalHidden);

    window.showPoi = (poiType, poiId, seed = null) => {
      console.info('showPoi', poiType, poiId);
      this.poiId = poiId;
      this.poiType = poiType;
      // Replace any previously-rendered POI immediately so we never show
      // the previous click's data while the new fetch is in flight.
      this.poi = seed;
      this.serpData = null;
      this.getPOI();
      Modal.getInstance(element).show();
    };
  },

  beforeUnmount() {
    const element = document.getElementById('poi-detail-dialog');
    element.removeEventListener('hidden.bs.modal', this.onModalHidden);
    Modal.getInstance(element).dispose();
    window.showPoi = null;
  },

  methods: {
    /* ─────────────── Modal lifecycle ─────────────── */
    onModalHidden() {
      this.poi = this.poiId = this.poiType = this.serpData = null;
    },

    /* ─────────────── Waypoints ───────────────────── */
    addToWaypoints() {
      if (!window.user) {
        window.onBoardingVue.launchFor(OnBoardingGoal.Waypoints);
        return;
      }
      const type = POIUtility.fromString(this.type);
      this.$store.dispatch('waypoints/createWaypoint', {
        lngLat: this.lngLat,
        title: this.name,
        type: type.poi,
        description: poiDetailsToWaypointDescriptionHelper(this.poi),
      });
      Modal.getInstance(document.getElementById('poi-detail-dialog')).hide();
    },

    addControlPoint() {
      if (!this.poi || !this.$store.state.planTrail) return;
      
      // Close the modal first
      Modal.getInstance(document.getElementById('poi-detail-dialog')).hide();
      
      // Add control point at POI location
      this.$store.dispatch('planTrail/addMarkerAtCoordinates', this.lngLat);
    },

    /* ─────────────── Hike highlights ─────────────── */
    makeHikeHighlight() {
      if (!this.poi || this.$store.state.planTrail === undefined) return;

      const { lng, lat } = this.lngLat;
      this.$store.commit('planTrail/addHikeHighlight', poiToHikeHighlight({
        type: this.type,
        name: this.name,
        serpName: this.serpName,
        description: this.description,
        lng,
        lat,
      }));
      this.$store.dispatch('planTrail/refreshHighlights');

      Modal.getInstance(document.getElementById('poi-detail-dialog')).hide();
    },

    /* ─────────────── Data fetch ──────────────────── */
    async getPOI() {
      if (!this.poiId || !this.poiType)
        throw new Error('POI id/type missing');

      const requestedId = this.poiId;
      const requestedType = this.poiType;

      try {
        this.showLoader = true;

        const { poi } = await graph_client.request(GetPOIByID, {
          type: requestedType,
          id: requestedId,
        });

        // Bail if the user clicked a different POI while this request was in flight
        if (this.poiId !== requestedId || this.poiType !== requestedType) return;

        this.poi = poi;

        const placeId =
          poi.place_id || poi.google_place_id || poi.placeId || null;

        let result;
        if (placeId && this.isProbablyGooglePlaceId(placeId)) {
          result = await serpClient.place(placeId);
        } else {
          const position = { lat: poi.lonlat[1], lng: poi.lonlat[0] };
          result = await serpClient.local({
            name: poi.name ?? '',
            otherInfo: poi.type ?? '',
            position,
          });
        }

        if (this.poiId !== requestedId || this.poiType !== requestedType) return;

        this.serpData = { result };
        if (result.place_id && this.poiId) {
          if (poi.klass == "Accommodation") {
            graph_client.request(UPDATE_ACCOMMODATION_MUTATION, {
              input: {
                id: this.poiId,
                attrs: {
                  placeId: result.place_id,
                },
              },
            });
          } else {
            graph_client.request(UPDATE_AMENITY_MUTATION, {
              input: {
                id: this.poiId,
                attrs: {
                  placeId: result.place_id,
                },
              },
            })
          }
        } else if (result.place_id && !this.poiId) {
          console.warn('POI ID missing, skipping placeId update');
        }
      } catch (err) {
        console.warn('SerpApi lookup failed:', err);
        if (this.poiId === requestedId && this.poiType === requestedType) {
          this.serpData = null;
        }
      } finally {
        // Only clear the loader if this is still the active request; otherwise a
        // superseded fetch would hide the loader for a request still in flight.
        if (this.poiId === requestedId && this.poiType === requestedType) {
          this.showLoader = false;
        }
      }
    },

    isProbablyGooglePlaceId(id) {
      const pattern = /^ChIJ[a-zA-Z0-9_-]{20,}$/;
      return pattern.test(id);
    },

    /* ─────────────── Free Views Management ─────────────── */
    getFreeViewsRemaining() {
      const stored = localStorage.getItem('hiiker_serp_free_views_remaining');
      return stored !== null ? parseInt(stored, 10) : 2;
    },

    setFreeViewsRemaining(count) {
      localStorage.setItem('hiiker_serp_free_views_remaining', count.toString());
    },

    loadViewedPoiIds() {
      const stored = localStorage.getItem('hiiker_serp_viewed_poi_ids');
      this.viewedPoiIds = stored ? JSON.parse(stored) : [];
    },

    saveViewedPoiIds() {
      localStorage.setItem('hiiker_serp_viewed_poi_ids', JSON.stringify(this.viewedPoiIds));
    },

    hasViewedCurrentPoi() {
      return this.viewedPoiIds.includes(this.poiId);
    },

    showToast(message) {
      this.toastMessage = message;
      const toastElement = document.getElementById('serpFreeViewToast');
      const toast = new Toast(toastElement, {
        autohide: true,
        delay: 5000
      });
      toast.show();
    },

    goToPurchasePage() {
      // If this POI has already been viewed for free, just reveal it
      if (this.hasViewedCurrentPoi()) {
        return; // shouldBlurSerpData will handle showing it
      }

      // If they have free views remaining, use one
      if (this.freeViewsRemaining > 0) {
        const newCount = this.freeViewsRemaining - 1;

        // Update both reactive data and localStorage
        this.freeViewsRemaining = newCount;
        this.setFreeViewsRemaining(newCount);

        // Add this POI to the viewed list
        this.viewedPoiIds.push(this.poiId);
        this.saveViewedPoiIds();

        // Show toast with remaining views
        if (newCount > 0) {
          this.showToast(`${newCount} free location view${newCount !== 1 ? 's' : ''} remaining`);
        } else {
          this.showToast('This was your last free location view. Upgrade to Pro+ for unlimited access!');
        }

        // Force re-render by updating the component
        this.$forceUpdate();
      } else {
        // No free views left, redirect to purchases
        window.location.href = '/purchases';
      }
    },

    /* ─────────────── Helpers ─────────────────────── */
    photoSrc(img) {
      // 1. SerpApi rich result → serpapi_thumbnail
      if (img.serpapi_thumbnail) return img.serpapi_thumbnail;
      if (img.thumbnail) return img.thumbnail;

      // 2. Fallback for Google photo-reference objects
      if (img.photo_reference) {
        const key = import.meta.env.VITE_GOOGLE_PLACES_KEY;
        return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=1000&photoreference=${img.photo_reference}&key=${key}`;
      }
      return '';
    },
  },

  computed: {
    /* ─────────────── Store checks ───────────────── */
    showAddControlPoint() {
      // Check if planTrail store is available
      return this.$store && this.$store.state.planTrail !== undefined;
    },

    showMakeHighlight() {
      // Only inside the planner, where the hikeHighlights store exists
      return this.$store && this.$store.state.planTrail !== undefined;
    },
    
    /* ─────────────── DB POI basics ───────────────── */
    type() { return this.poi ? this.poi.type : ''; },
    name() { return this.poi ? this.poi.name : ''; },
    phoneNumber() { return this.poi ? this.poi.international_phone_number : ''; },
    website() {
      if (!this.poi || !this.poi.website) return '';
      return /^https?:\/\/$/.test(this.poi.website) ? '' : this.poi.website;
    },
    email() { return this.poi ? this.poi.email : ''; },
    description() { return this.poi ? this.poi.description : ''; },
    coords() {
      if (!this.poi) return '';
      const [lon, lat] = this.poi.lonlat;
      return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    },
    lngLat() {
      if (!this.poi) throw new Error('POI null');
      const [lng, lat] = this.poi.lonlat;
      return { lng, lat };
    },
    coordsLink() {
      if (!this.poi) return '#';
      const [lon, lat] = this.poi.lonlat;
      return `/map?lng=${lon.toFixed(5)}&lat=${lat.toFixed(5)}`;
    },

    /* ─────────────── SerpApi result ─────────────── */
    serpResult() { return this.serpData?.result || null; },

    /* Check if SERP data should be blurred */
    shouldBlurSerpData() {
      if (this.isProPlus) return false;

      // If this POI has been viewed with a free view, don't blur
      if (this.hasViewedCurrentPoi()) return false;

      // Check if there's contact info (phone, email, or description)
      const hasContactInfo = !!(this.serpPhone || this.email || this.description);

      // Check if there are photos or reviews
      const hasMediaOrReviews = this.serpImages.length > 0 || this.serpReviews.length > 0;

      // Only blur if both conditions are met
      return hasContactInfo && hasMediaOrReviews;
    },

    viewLocationButtonText() {
      if (this.freeViewsRemaining > 0) {
        return `View Location Information (${this.freeViewsRemaining} free view${this.freeViewsRemaining !== 1 ? 's' : ''} remaining)`;
      }
      return 'Upgrade to PRO+ to view more information';
    },

    serpName() { return this.serpResult?.name || ''; },
    serpRating() { return this.serpResult?.rating || 0; },
    serpTotalRatings() { return this.serpResult?.user_ratings_total || 0; },
    serpPhone() {
      const r = this.serpResult;
      return r?.international_phone_number || r?.formatted_phone_number || r?.phone || '';
    },
    serpWebsite() { return this.serpResult?.website || ''; },
    serpAddress() { return this.serpResult?.formatted_address || this.serpResult?.address || ''; },

    /* Images: rich (`images`) → thin (`photos`) */
    serpImages() {
      if (!this.serpResult) return [];
      if (Array.isArray(this.serpResult.images) && this.serpResult.images.length)
        return this.serpResult.images;
      if (Array.isArray(this.serpResult.photos) && this.serpResult.photos.length)
        return this.serpResult.photos;
      return [];
    },

    /* Reviews: new shape (`user_reviews.most_relevant`) or legacy array */
    serpReviews() {
      const r = this.serpResult;
      if (!r) return [];

      // New rich payload
      if (r.user_reviews?.most_relevant?.length)
        return r.user_reviews.most_relevant.map((rev) => ({
          author: rev.username || rev.author_name || 'Anonymous',
          rating: rev.rating || 0,
          date: rev.date || rev.relative_time_description || '',
          text: rev.description || rev.text || '',
        }));

      // Previous design (already an array of {author_name, rating, text,…})
      if (Array.isArray(r.reviews))
        return r.reviews.map((rev) => ({
          author: rev.author_name || 'Anonymous',
          rating: rev.rating || 0,
          date: rev.relative_time_description || '',
          text: rev.text || '',
        }));

      return [];
    },
  },
});
</script>

<style scoped>
/* minimal tweaks for stars/text */
.text-warning { color: #ffb400; }
.text-yellow  { color: #ffb400; }

/* SERP data blur effect for non-Pro+ users */
.serp-wrapper {
  position: relative;
}

.serp-data-container {
  /* No position needed here */
}

.serp-blurred {
  filter: blur(10px);
  pointer-events: none;
  user-select: none;
}

.serp-overlay {
  position: absolute;
  top: 4%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: auto;
}

.btn-yellow {
  background-color: #FBCD01;
  color: #000;
  border: none;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
}

.btn-yellow:hover {
  background-color: #e6ba00;
  color: #000;
}

/* Toast styling */
.bg-yellow {
  background-color: #FBCD01 !important;
  color: #000;
}

.toast-body {
  color: #000;
  background-color: #fff;
}
</style>
